import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Paper,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import { formatText, formatTextForDatabase } from '../../common/TextFormatterProvider';
import { getTypedValueLabel, normalizeCategoryType, toSelectionOption } from '../../../utils/tagValueUtils';

const dataTypes = ['Text', 'Number', 'Date', 'Yes/No'];

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 500,
}));

function ColumnDefinition({ identifier, attributeColumns, tableAttributeColumns = [], detailColumns, attributeCategories = [], categoryValues = {}, tableType, onChange }) {
  const isRaw = tableType === 'raw';
  const getCategoryTypeForName = (categoryName) => {
    const category = attributeCategories.find((cat) => cat.id === categoryName);
    return normalizeCategoryType(category?.type);
  };

  const getCategoryOptions = (categoryName) => {
    return (categoryValues[categoryName] || []).map((value, index) => toSelectionOption(value, `${categoryName}-${index}`));
  };

  const handleIdentifierChange = (field) => (event) => {
    const value = event.target.value;

    onChange('identifier', {
      ...identifier,
      [field]: value
    });
  };

  const handleColumnChange = (type, index, field) => (event) => {
    const value = event.target.value;

    const columns = type === 'attributeColumns' ? [...attributeColumns] : [...detailColumns];
    columns[index] = {
      ...columns[index],
      [field]: value
    };
    if (type === 'attributeColumns' && field === 'name') {
      columns[index].type = getCategoryTypeForName(value);
    }
    // Classes are declared on Text detail columns only (CreateTableForm's Classes step).
    if (type === 'detailColumns' && field === 'type' && value !== 'Text') {
      delete columns[index].classification;
    }
    onChange(type, columns);
  };

  // An attribute column's upload-tag requirement. Detail columns declare theirs,
  // per file and per unit, in the form's Mandatory Columns step.
  const handleAttributeMandatoryChange = (index) => (event) => {
    const columns = [...attributeColumns];
    columns[index] = {
      ...columns[index],
      mandatory: event.target.checked
    };
    onChange('attributeColumns', columns);
  };

  const handleIndexChange = (type, index) => (event) => {
    const columns = type === 'attributeColumns' ? [...attributeColumns] : [...detailColumns];
    columns[index] = {
      ...columns[index],
      index: event.target.checked
    };
    onChange(type, columns);
  };

  const handleAddColumn = (type) => {
    const newColumn = {
      name: '',
      type: 'Text',
      description: '',
      ...(type === 'detailColumns' ? { index: false } : { mandatory: false })
    };
    const columns = type === 'attributeColumns' ? [...attributeColumns] : [...detailColumns];
    onChange(type, [...columns, newColumn]);
  };

  const handleDeleteColumn = (type, index) => {
    const columns = type === 'attributeColumns' ? [...attributeColumns] : [...detailColumns];
    columns.splice(index, 1);
    onChange(type, columns);
  };

  // Table attribute column handlers
  const handleAddTableAttribute = () => {
    const updated = [...tableAttributeColumns, { name: '', type: 'Text', value: '', description: '' }];
    onChange('tableAttributeColumns', updated);
  };

  const handleTableAttributeChange = (index, field) => (event) => {
    const updated = [...tableAttributeColumns];
    const value = field === 'name'
      ? formatTextForDatabase(event.target.value)
      : event.target.value;
    updated[index] = { ...updated[index], [field]: value };
    // Clear value when category changes
    if (field === 'name') {
      updated[index].value = '';
      updated[index].type = getCategoryTypeForName(value);
    }
    onChange('tableAttributeColumns', updated);
  };

  const handleDeleteTableAttribute = (index) => {
    const updated = [...tableAttributeColumns];
    updated.splice(index, 1);
    onChange('tableAttributeColumns', updated);
  };

  // A category taken as a table attribute is one fixed column of the table, so it
  // is not offered as an ordinary attribute too (00 N.3). A row already holding it
  // keeps it shown, and the form's duplicate-name check refuses the pair.
  const tableAttributeCategoryIds = new Set(tableAttributeColumns.map((column) => column.name));
  const attributeChoices = (column) => attributeCategories.filter(
    (cat) => !tableAttributeCategoryIds.has(cat.id) || cat.id === column.name,
  );

  const renderColumnFields = (column, index, type) => (
    <Grid container spacing={2} alignItems="center" key={index}>
      <Grid item xs={4}>
        {type === 'attributeColumns' ? (
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={column.name || ''}
              label="Category"
              onChange={handleColumnChange(type, index, 'name')}
            >
              {attributeChoices(column).map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{formatText(cat.name || cat.id)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            label="Column Name"
            value={column.name}
            onChange={handleColumnChange(type, index, 'name')}
            size="small"
            required
          />
        )}
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Description"
          value={column.description || ''}
          onChange={handleColumnChange(type, index, 'description')}
          size="small"
        />
      </Grid>
      <Grid item xs={2}>
        {type === 'attributeColumns' ? (
          <TextField
            fullWidth
            size="small"
            label="Type"
            value={getCategoryTypeForName(column.name)}
            disabled
          />
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={column.type || 'Text'}
              label="Type"
              onChange={handleColumnChange(type, index, 'type')}
            >
              {dataTypes.map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>
      {type === 'attributeColumns' && (
        <Grid item xs={2}>
          <FormControlLabel
            control={
              <Switch
                checked={column.mandatory || false}
                onChange={handleAttributeMandatoryChange(index)}
                size="small"
              />
            }
            label="Mandatory"
            labelPlacement="start"
            sx={{ marginLeft: 0 }}
          />
        </Grid>
      )}
      {type === 'detailColumns' && (
        <Grid item xs={2}>
          <FormControlLabel
            control={
              <Switch
                checked={column.index || false}
                onChange={handleIndexChange(type, index)}
                size="small"
              />
            }
            label="Index"
            labelPlacement="start"
            sx={{ marginLeft: 0 }}
          />
        </Grid>
      )}
      <Grid item xs={1}>
        <IconButton
          onClick={() => handleDeleteColumn(type, index)}
          size="small"
          color="error"
        >
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Grid>
  );

  const renderTableAttributeFields = (column, index) => {
    const values = getCategoryOptions(column.name);
    return (
      <Grid container spacing={2} alignItems="center" key={index}>
        <Grid item xs={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={column.name || ''}
              label="Category"
              onChange={handleTableAttributeChange(index, 'name')}
            >
              {attributeCategories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{formatText(cat.name || cat.id)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Value</InputLabel>
            <Select
              value={column.value ?? ''}
              label="Value"
              onChange={handleTableAttributeChange(index, 'value')}
              disabled={!column.name}
            >
              {values.map((val) => (
                <MenuItem key={String(val.id || val.value)} value={val.value}>
                  {formatText(getTypedValueLabel(val.value, val.type, val.name))}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <TextField
            fullWidth
            size="small"
            label="Type"
            value={getCategoryTypeForName(column.name)}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Description"
            value={column.description || ''}
            onChange={handleTableAttributeChange(index, 'description')}
            size="small"
          />
        </Grid>
        <Grid item xs={1}>
          <IconButton
            onClick={() => handleDeleteTableAttribute(index)}
            size="small"
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {!isRaw && (
        <StyledPaper>
          <SectionTitle variant="h6">
            Identifier Column
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Column Name"
                value={identifier.name}
                onChange={handleIdentifierChange('name')}
                size="small"
                required
              />
            </Grid>
            <Grid item xs={3}>
              {/* Identifiers are always stored as text (BigQuery STRING keys); the backend
                  rejects a non-Text identifier. */}
              <TextField
                fullWidth
                size="small"
                label="Type"
                value="Text"
                disabled
                helperText="Identifiers are stored as text"
              />
            </Grid>
          </Grid>
        </StyledPaper>
      )}

      {/* Attribute Columns */}
      <StyledPaper>
        <SectionTitle variant="h6">
          Attribute Columns
        </SectionTitle>
        <Box sx={{ mb: 2 }}>
          {attributeColumns.map((column, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {renderColumnFields(column, index, 'attributeColumns')}
            </Box>
          ))}
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAddColumn('attributeColumns')}
          variant="outlined"
          size="small"
        >
          Add Attribute Column
        </Button>
      </StyledPaper>

      {/* Table Attribute Columns */}
      <StyledPaper>
        <SectionTitle variant="h6">
          Table Attributes
        </SectionTitle>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
          Constant values applied to every row in this table. Useful for cross-table analysis, exports, and formula mappings.
        </Typography>
        <Box sx={{ mb: 2 }}>
          {tableAttributeColumns.map((column, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {renderTableAttributeFields(column, index)}
            </Box>
          ))}
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddTableAttribute}
          variant="outlined"
          size="small"
        >
          Add Table Attribute
        </Button>
      </StyledPaper>

      {/* Details Columns */}
      <StyledPaper>
        <SectionTitle variant="h6">
          {isRaw ? 'Columns' : 'Details Columns'}
        </SectionTitle>
        <Box sx={{ mb: 2 }}>
          {detailColumns.map((column, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {renderColumnFields(column, index, 'detailColumns')}
            </Box>
          ))}
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAddColumn('detailColumns')}
          variant="outlined"
          size="small"
        >
          {isRaw ? 'Add Column' : 'Add Detail Column'}
        </Button>
      </StyledPaper>
    </Box>
  );
}

export default ColumnDefinition;
